import { defineStore } from 'pinia'
import axios from './api'
import { _ElMessage } from '@/utils/element-plus-wrapper'
import { triggerBlobDownload } from '@/utils/download'
import type {
  ExternalCustomer,
  ExternalCustomerItem,
  CustomerItemListResponse,
  CustomerListResponse,
  Customer,
  CustomerDetail,
  GetCustomersQuery,
  GetSalesrepsQuery,
  SalesRep,
  CustomerProfile,
  UpdateCustomerProfilePayload,
  UpdateCustomerPayload,
  UpdateCustomerAutoHeadsupPayload,
  UpdateCustomerPriorityPayload,
  CustomerSchedule,
  CustomerScheduleListResponse,
  GetCustomerSchedulesQuery,
  CustomerArListItem,
  CustomerArListResponse,
  GetCustomerArQuery,
} from '@/types/customer.type'
import {
  CustomerOpenStatusQuery,
  CustomerTargetQuery,
  CustomerTargetType,
} from '@/types/customer.type'
import type {
  CustomerPriorityChangeLog,
  CustomerPriorityChangeLogListResponse,
  GetCustomerPriorityChangeLogsQuery,
  CustomerArChangeLog,
  CustomerArChangeLogListResponse,
  GetCustomerArChangeLogsQuery,
} from '@/types/change-log.type'
import type { PaginationMeta, PaginationResponse } from '@/types/pagination.type'
import type { GetBranchItemsQuery, Item } from '@/types/item.type'
import type { FreeNote } from '@/types/free-note.type'

// Customer Listのfetch上限
export const CUSTOMER_FETCH_LIMIT = 200

// 古い応答が新しい応答を上書きしないようにするための世代カウンタ
let externalCustomersRequestSeq = 0

interface State {
  external_customers: ExternalCustomer[]
  external_customer_items: ExternalCustomerItem[]
  customers: Customer[]
  customers_stats: PaginationMeta | null
  // customers とは共有できない（SalesrepSettingsDialog が同 view で担当顧客全件を載せる）
  watchlistCustomers: Customer[]
  customersByIds: Customer[]
  search_customers_result: Customer[]
  currentCustomer: CustomerDetail | null
  currentCustomerItems: Item[]
  branchItems: Item[]
  salesreps: SalesRep[]
  salesreps_stats: PaginationMeta | null
  search_salesreps_result: SalesRep[] // サーバが参照範囲の担当者に絞った結果
  customer_schedules: CustomerSchedule[]
  customer_schedules_stats: PaginationMeta | null
  customer_ar: CustomerArListItem[]
  customer_ar_stats: PaginationMeta | null
  customer_priority_change_logs: CustomerPriorityChangeLog[]
  customer_priority_change_logs_stats: PaginationMeta | null
  customer_ar_change_logs: CustomerArChangeLog[]
  customer_ar_change_logs_stats: PaginationMeta | null
}

export const useCustomerStore = defineStore('customerStore', {
  state: (): State => ({
    external_customers: [],
    external_customer_items: [],
    customers: [],
    customers_stats: null,
    watchlistCustomers: [],
    customersByIds: [],
    currentCustomer: null,
    currentCustomerItems: [],
    branchItems: [],
    salesreps: [],
    salesreps_stats: null,
    search_customers_result: [],
    search_salesreps_result: [],
    customer_schedules: [],
    customer_schedules_stats: null,
    customer_ar: [],
    customer_ar_stats: null,
    customer_priority_change_logs: [],
    customer_priority_change_logs_stats: null,
    customer_ar_change_logs: [],
    customer_ar_change_logs_stats: null,
  }),
  getters: {
    sortedBranchItems: (state): Item[] =>
      [...state.branchItems].sort((a, b) =>
        a.itemNumber.localeCompare(b.itemNumber, undefined, { numeric: true }),
      ),
  },
  actions: {
    clearExternalCustomers() {
      this.$patch((state) => {
        state.external_customers = []
      })
    },
    clearExternalCustomerItems() {
      this.$patch((state) => {
        state.external_customer_items = []
      })
    },
    // resolve 値は「この応答が最新で state に反映されたか」（古い応答なら false）
    getExternalCustomerCandidates(params?: { org?: string; q?: string }) {
      const requestSeq = ++externalCustomersRequestSeq
      return new Promise<boolean>((resolve, reject) => {
        axios
          .get<CustomerListResponse>('/er-customers/me', {
            params: {
              limit: CUSTOMER_FETCH_LIMIT,
              ...(params?.org ? { org: params.org } : {}),
              ...(params?.q ? { q: params.q } : {}),
            },
          })
          .then((res) => {
            if (requestSeq !== externalCustomersRequestSeq) {
              resolve(false)
              return
            }
            this.$patch((state) => {
              state.external_customers = res.data.customers
            })
            resolve(true)
          })
          .catch((err) => {
            if (err.response?.status === 403) {
              _ElMessage({ type: 'error', message: 'Permission denied.' })
            } else {
              _ElMessage({ type: 'error', message: 'Failed to fetch customers' })
            }
            console.error('Failed to fetch customers:', err)
            reject(err)
          })
      })
    },
    getExternalCustomerItems(accountNumber: number) {
      axios
        .get<CustomerItemListResponse>(`/er-customers/${accountNumber}/customer-items`)
        .then((res) => {
          this.$patch((state) => {
            state.external_customer_items = res.data.items
          })
        })
        .catch((err) => {
          if (err.response?.status === 403) {
            _ElMessage({ type: 'error', message: 'Permission denied.' })
          } else {
            _ElMessage({ type: 'error', message: 'Failed to load customer items.' })
          }
          console.error('Failed to fetch customer items:', err)
        })
    },
    // customers
    clearCustomers() {
      this.$patch((state) => {
        state.customers = []
        state.customers_stats = null
        state.search_customers_result = []
      })
    },
    clearCurrentCustomer() {
      this.$patch((state) => {
        state.currentCustomer = null
        state.currentCustomerItems = []
      })
    },
    clearCustomerSearchResult() {
      this.$patch((state) => {
        state.search_customers_result = []
      })
    },
    getCustomers(query: GetCustomersQuery) {
      axios
        .get('/customers', { params: query })
        .then((res) => {
          this.$patch((state) => {
            state.customers = res.data.items
            state.customers_stats = res.data.meta
          })
        })
        .catch((err) => {
          _ElMessage({ type: 'error', message: 'Failed to load customers.' })
          console.error('Failed to fetch customers:', err)
        })
    },
    // 顧客の更新は呼び出し側で state を列挙せずここに通す。watchlist の出入りは
    // 列の書き換えではないので updateCustomer 側で扱う
    patchCustomer(customer: CustomerDetail) {
      this.$patch((state) => {
        const apply = (c: Customer): Customer => (c.id === customer.id ? { ...c, ...customer } : c)
        state.customers = state.customers.map(apply)
        state.watchlistCustomers = state.watchlistCustomers.map(apply)
        state.customersByIds = state.customersByIds.map(apply)
        state.search_customers_result = state.search_customers_result.map(apply)
        if (state.currentCustomer?.id === customer.id) {
          state.currentCustomer = { ...state.currentCustomer, ...customer }
        }
      })
    },
    // pin の source of truth は Customer 側の列。pin 先は一覧型に載らない
    // （API が list で relation を引かない）ので currentCustomer だけを更新する
    setPinnedFreeNote(note: FreeNote) {
      this.$patch((state) => {
        if (state.currentCustomer?.id === note.customerId) {
          state.currentCustomer.pinnedFreeNoteId = note.id
          state.currentCustomer.pinnedFreeNote = note
        }
      })
    },
    refreshPinnedFreeNote(note: FreeNote) {
      this.$patch((state) => {
        if (state.currentCustomer?.pinnedFreeNoteId === note.id) {
          state.currentCustomer.pinnedFreeNote = note
        }
      })
    },
    // API は pin 先が当該 note のときだけ customer を更新するので、front も同じ条件で外す
    clearPinnedFreeNote(noteId: number) {
      this.$patch((state) => {
        if (state.currentCustomer?.pinnedFreeNoteId === noteId) {
          state.currentCustomer.pinnedFreeNoteId = null
          state.currentCustomer.pinnedFreeNote = null
        }
      })
    },
    clearCustomersByIds() {
      this.$patch((state) => {
        state.customersByIds = []
      })
    },
    getCustomersByIds(ids: number[], skipSave = false) {
      return new Promise<Customer[]>((resolve, reject) => {
        axios
          .get<Customer[]>('/customers/by-ids', { params: { ids: ids.join(',') } })
          .then((res) => {
            if (!skipSave) {
              this.$patch((state) => {
                state.customersByIds = res.data
              })
            }
            resolve(res.data)
          })
          .catch((err) => {
            _ElMessage({ type: 'error', message: 'Failed to load customers.' })
            console.error('Failed to fetch customers by ids:', err)
            reject(err)
          })
      })
    },
    downloadCsv(query: GetCustomersQuery) {
      axios
        .get<Blob>('/customers/download', { params: query, responseType: 'blob' })
        .then((res) => {
          const disposition = res.headers['content-disposition']
          const filename = disposition?.match(/filename="?([^";]+)"?/i)?.[1] ?? 'customers.csv'
          triggerBlobDownload(res.data, filename)
        })
        .catch((err) => {
          _ElMessage({ type: 'error', message: 'Failed to download CSV.' })
          console.error('Failed to download customers CSV:', err)
        })
    },
    downloadProfileCsv(salesrepNumber: number, openStatus: CustomerOpenStatusQuery) {
      axios
        .get<Blob>('/customers/download-profile', {
          params: { salesrep_number: salesrepNumber, openStatus },
          responseType: 'blob',
        })
        .then((res) => {
          const disposition = res.headers['content-disposition']
          const filename =
            disposition?.match(/filename="?([^";]+)"?/i)?.[1] ?? 'customer-profiles.csv'
          triggerBlobDownload(res.data, filename)
        })
        .catch((err) => {
          _ElMessage({ type: 'error', message: 'Failed to download CSV.' })
          console.error('Failed to download customer profiles CSV:', err)
        })
    },
    clearWatchlistCustomers() {
      this.$patch((state) => {
        state.watchlistCustomers = []
      })
    },
    // 実運用上少数のため 1 ページで取り切る
    getWatchlistCustomers(salesrepNumber: number) {
      axios
        .get<PaginationResponse<Customer>>('/customers', {
          params: {
            page: 1,
            limit: 500,
            salesrep_number: salesrepNumber,
            customerTargetType: CustomerTargetQuery.Watch,
            openStatus: CustomerOpenStatusQuery.open,
          },
        })
        .then((res) => {
          this.$patch((state) => {
            state.watchlistCustomers = res.data.items
          })
        })
        .catch((err) => {
          _ElMessage({ type: 'error', message: 'Failed to load customers.' })
          console.error('Failed to fetch watchlist customers:', err)
        })
    },
    clearCustomerSchedules() {
      this.$patch((state) => {
        state.customer_schedules = []
        state.customer_schedules_stats = null
      })
    },
    getCustomerSchedules(query: GetCustomerSchedulesQuery) {
      axios
        .get<CustomerScheduleListResponse>('/customers/schedules', { params: query })
        .then((res) => {
          this.$patch((state) => {
            state.customer_schedules = res.data.items
            state.customer_schedules_stats = res.data.meta
          })
        })
        .catch((err) => {
          _ElMessage({ type: 'error', message: 'Failed to load customer schedules.' })
          console.error('Failed to fetch customer schedules:', err)
        })
    },
    clearCustomerAr() {
      this.$patch((state) => {
        state.customer_ar = []
        state.customer_ar_stats = null
      })
    },
    getCustomerAr(query: GetCustomerArQuery) {
      axios
        .get<CustomerArListResponse>('/customers/ar', { params: query })
        .then((res) => {
          this.$patch((state) => {
            state.customer_ar = res.data.items
            state.customer_ar_stats = res.data.meta
          })
        })
        .catch((err) => {
          _ElMessage({ type: 'error', message: 'Failed to load customer AR.' })
          console.error('Failed to fetch customer AR:', err)
        })
    },
    getCustomer(id: number) {
      axios
        .get<CustomerDetail>(`/customers/${id}`)
        .then((res) => {
          this.$patch((state) => {
            state.currentCustomer = res.data
          })
        })
        .catch((err) => {
          _ElMessage({ type: 'error', message: 'Failed to load customer.' })
          console.error('Failed to fetch customer:', err)
        })
    },
    updateCustomerProfile(id: number, payload: UpdateCustomerProfilePayload) {
      return new Promise<CustomerProfile>((resolve, reject) => {
        axios
          .patch<CustomerProfile>(`/customers/${id}/profile`, payload)
          .then((res) => {
            this.$patch((state) => {
              if (state.currentCustomer?.id === id) {
                state.currentCustomer = { ...state.currentCustomer, profile: res.data }
              }
            })
            _ElMessage({ type: 'success', message: 'Customer updated.' })
            resolve(res.data)
          })
          .catch((err) => {
            _ElMessage({ type: 'error', message: 'Failed to update customer.' })
            console.error('Failed to update customer:', err)
            reject(err)
          })
      })
    },
    updateCustomer(id: number, payload: UpdateCustomerPayload) {
      return new Promise<CustomerDetail>((resolve, reject) => {
        axios
          .patch<CustomerDetail>(`/customers/${id}`, payload)
          .then((res) => {
            this.patchCustomer(res.data)
            this.$patch((state) => {
              // watch 以外への変更は watchlist から外れるので、置換ではなく入れ替えで反映する
              state.watchlistCustomers = [
                ...state.watchlistCustomers.filter((c) => c.id !== id),
                ...(res.data.customerTargetType === CustomerTargetType.Watch ? [res.data] : []),
              ]
            })
            _ElMessage({ type: 'success', message: 'Customer updated.' })
            resolve(res.data)
          })
          .catch((err) => {
            _ElMessage({ type: 'error', message: 'Failed to update customer.' })
            console.error('Failed to update customer:', err)
            reject(err)
          })
      })
    },
    // updateCustomer と同じ PATCH /customers/:id だが、per-row トグル用途で別 action にしている。
    updateAutoHeadsup(id: number, payload: UpdateCustomerAutoHeadsupPayload) {
      return new Promise<CustomerDetail>((resolve, reject) => {
        axios
          .patch<CustomerDetail>(`/customers/${id}`, payload)
          .then((res) => {
            this.patchCustomer(res.data)
            resolve(res.data)
          })
          .catch((err) => {
            _ElMessage({ type: 'error', message: 'Failed to update customer.' })
            console.error('Failed to update auto headsup:', err)
            reject(err)
          })
      })
    },
    // updateCustomer と同じ PATCH /customers/:id だが、watchlist の出入りを伴わないので別 action。
    updateCustomerPriority(id: number, payload: UpdateCustomerPriorityPayload) {
      return new Promise<CustomerDetail>((resolve, reject) => {
        axios
          .patch<CustomerDetail>(`/customers/${id}`, payload)
          .then((res) => {
            this.patchCustomer(res.data)
            _ElMessage({ type: 'success', message: 'Customer updated.' })
            resolve(res.data)
          })
          .catch((err) => {
            _ElMessage({ type: 'error', message: 'Failed to update customer.' })
            console.error('Failed to update customer priority:', err)
            reject(err)
          })
      })
    },
    closeCustomer(id: number) {
      axios
        .post(`/customers/${id}/close`)
        .then(() => {
          this.$patch((state) => {
            state.customers = state.customers.filter((c) => c.id !== id)
            if (state.customers_stats) state.customers_stats.totalItems -= 1
            state.watchlistCustomers = state.watchlistCustomers.filter((c) => c.id !== id)
          })
          _ElMessage({ type: 'success', message: 'Customer marked as closed.' })
        })
        .catch((err) => {
          _ElMessage({ type: 'error', message: 'Failed to update customer.' })
          console.error('Failed to close customer:', err)
        })
    },
    reopenCustomer(id: number) {
      axios
        .post(`/customers/${id}/reopen`)
        .then(() => {
          this.$patch((state) => {
            state.customers = state.customers.filter((c) => c.id !== id)
            if (state.customers_stats) state.customers_stats.totalItems -= 1
          })
          _ElMessage({ type: 'success', message: 'Customer reopened.' })
        })
        .catch((err) => {
          _ElMessage({ type: 'error', message: 'Failed to update customer.' })
          console.error('Failed to reopen customer:', err)
        })
    },
    clearCustomerPriorityChangeLogs() {
      this.$patch((state) => {
        state.customer_priority_change_logs = []
        state.customer_priority_change_logs_stats = null
      })
    },
    getCustomerPriorityChangeLogs(customerId: number, query: GetCustomerPriorityChangeLogsQuery) {
      axios
        .get<CustomerPriorityChangeLogListResponse>(
          `/customers/${customerId}/priority-change-logs`,
          { params: query },
        )
        .then((res) => {
          this.$patch((state) => {
            state.customer_priority_change_logs = res.data.items
            state.customer_priority_change_logs_stats = res.data.meta
          })
        })
        .catch((err) => {
          _ElMessage({ type: 'error', message: 'Failed to fetch customer priority change logs.' })
          console.error('Failed to fetch customer priority change logs:', err)
        })
    },
    clearCustomerArChangeLogs() {
      this.$patch((state) => {
        state.customer_ar_change_logs = []
        state.customer_ar_change_logs_stats = null
      })
    },
    getCustomerArChangeLogs(query: GetCustomerArChangeLogsQuery) {
      axios
        .get<CustomerArChangeLogListResponse>('/customers/ar-change-logs', { params: query })
        .then((res) => {
          this.$patch((state) => {
            state.customer_ar_change_logs = res.data.items
            state.customer_ar_change_logs_stats = res.data.meta
          })
        })
        .catch((err) => {
          _ElMessage({ type: 'error', message: 'Failed to load customer AR change logs.' })
          console.error('Failed to fetch customer AR change logs:', err)
        })
    },
    clearBranchItems() {
      this.$patch((state) => {
        state.branchItems = []
      })
    },
    getBranchItems(
      customerId: number,
      maguchiCategoryCode: number,
      query: GetBranchItemsQuery,
      { skipSave = false } = {},
    ) {
      return new Promise<Item[]>((resolve, reject) => {
        axios
          .get<Item[]>(`/customers/${customerId}/branch-items/${maguchiCategoryCode}`, {
            params: query,
          })
          .then((res) => {
            if (!skipSave) {
              this.$patch((state) => {
                state.branchItems = res.data
              })
            }
            resolve(res.data)
          })
          .catch((err) => {
            _ElMessage({ type: 'error', message: 'Failed to fetch branch items.' })
            console.error('Failed to fetch branch items:', err)
            reject(err)
          })
      })
    },
    getCustomerItems(id: number) {
      axios
        .get(`/customers/${id}/items`)
        .then((res) => {
          this.$patch((state) => {
            state.currentCustomerItems = res.data
          })
        })
        .catch((err) => {
          _ElMessage({ type: 'error', message: 'Failed to load customer items.' })
          console.error('Failed to fetch customer items:', err)
        })
    },
    searchCustomers(q: string, branchOrgId?: number, salesrep_number?: number) {
      return new Promise<void>((resolve, reject) => {
        axios
          .get('/customers/search', { params: { q, branchOrgId, salesrep_number } })
          .then((res) => {
            this.$patch((state) => {
              state.search_customers_result = res.data
            })
            resolve()
          })
          .catch((err) => {
            _ElMessage({ type: 'error', message: 'Failed to search customers.' })
            console.error('Failed to search customers:', err)
            reject(err)
          })
      })
    },
    clearSalesreps() {
      this.$patch((state) => {
        state.salesreps = []
        state.salesreps_stats = null
        state.search_salesreps_result = []
      })
    },
    clearSalesrepSearchResult() {
      this.$patch((state) => {
        state.search_salesreps_result = []
      })
    },
    getSalesreps(query: GetSalesrepsQuery) {
      axios
        .get('/customers/salesreps', { params: query })
        .then((res) => {
          this.$patch((state) => {
            state.salesreps = res.data.items
            state.salesreps_stats = res.data.meta
          })
        })
        .catch((err) => {
          _ElMessage({ type: 'error', message: 'Failed to load sales reps.' })
          console.error('Failed to fetch salesreps:', err)
        })
    },
    searchSalesreps(q: string) {
      return this.findSalesreps(q).then((salesreps) => {
        this.$patch((state) => {
          state.search_salesreps_result = salesreps
        })
      })
    },
    findSalesreps(q: string) {
      return new Promise<SalesRep[]>((resolve, reject) => {
        axios
          .get<SalesRep[]>('/customers/salesreps/search', { params: { q } })
          .then((res) => resolve(res.data))
          .catch((err) => {
            _ElMessage({ type: 'error', message: 'Failed to search sales reps.' })
            console.error('Failed to search salesreps:', err)
            reject(err)
          })
      })
    },
  },
})
